import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface PricingRow {
  id: string;
  model: string;
  provider: string;
  input_price_per_1k_tokens: number;
  output_price_per_1k_tokens: number;
  notes: string | null;
  updated_at: string;
}

type DataHookResult<T> = {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
};

interface UseAiPricingResult extends DataHookResult<PricingRow[]> {
  savePricingRow: (
    row: PricingRow,
    inputPrice: number,
    outputPrice: number
  ) => Promise<void>;
  savingId: string | null;
}

export function useAiPricing(): UseAiPricingResult {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["ai-pricing"],
    queryFn: async () => {
      const { data: rows, error: err } = await supabase
        .from("ai_model_pricing")
        .select("*")
        .order("provider")
        .order("model");
      if (err) throw new Error(err.message);
      return (rows || []) as PricingRow[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async ({
      id,
      inputPrice,
      outputPrice,
    }: {
      id: string;
      inputPrice: number;
      outputPrice: number;
    }) => {
      const { error: updateErr } = await supabase
        .from("ai_model_pricing")
        .update({
          input_price_per_1k_tokens: inputPrice,
          output_price_per_1k_tokens: outputPrice,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (updateErr) throw new Error(updateErr.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-pricing"] });
    },
  });

  return {
    data,
    isLoading,
    isError,
    error: error instanceof Error ? error : null,
    refetch: async () => {
      await refetch();
    },
    savePricingRow: async (row, inputPrice, outputPrice) => {
      await saveMutation.mutateAsync({
        id: row.id,
        inputPrice,
        outputPrice,
      });
    },
    savingId: saveMutation.isPending ? "pending" : null,
  };
}

export function invalidateAiPricing() {
  // Placeholder for cache invalidation pattern
}
